// Cache-bust: v20251110-090600
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import ABTStyleControls from '@/components/viewers/ABTStyleControls'
import AnnotationOverlay from '@/components/viewers/AnnotationOverlay'
import SavedAnnotationsOverlay from '@/components/viewers/SavedAnnotationsOverlay'
import SimpleMinimap from '@/components/viewers/SimpleMinimap'
import { AIDiagnosisPanel } from './AIDiagnosisPanel'
import { AnnotationToolPanel } from './AnnotationToolPanel'
import { CytologyReportPanel } from './CytologyReportPanel'
import { SnapshotManagerPanel } from '@/components/viewers/SnapshotManagerPanel'
import { SnapshotRegionOverlay } from '@/components/viewers/SnapshotRegionOverlay'
import { logger } from '@/utils/logger'

interface SlideInfo {
  id: string
  dimensions: {
    width: number
    height: number
  }
  tile_size: number
  levels: number  // OpenSlide levels
  level_dimensions: number[][]
  level_downsamples: number[]
  properties?: {
    'openslide.mpp-x'?: string
    'openslide.mpp-y'?: string
    'openslide.objective-power'?: string
    [key: string]: any
  }
  bounds?: {
    x: number
    y: number
    width: number
    height: number
  }
}

const ImprovedSVSViewer: React.FC = () => {
  // Debug: verify new code is loaded
  useEffect(() => {
    console.log("🔥🔥🔥 ImprovedSVSViewer LOADED - VERSION $(date +%H:%M:%S) 🔥🔥🔥");
  }, []);
  const { t } = useTranslation()
  const { imageId } = useParams<{ imageId: string }>()
  const [searchParams] = useSearchParams()
  const editReportId = searchParams.get('editReportId')
  const viewerRef = useRef<HTMLDivElement>(null)
  const viewerInstanceRef = useRef<any>(null)
  const [viewerReady, setViewerReady] = useState(false)
  const [zoomInfo, setZoomInfo] = useState({ zoom: 0, level: 0, magnification: 0 })
  const [scaleBarWidth, setScaleBarWidth] = useState(100)
  const [scaleBarValueMicrons, setScaleBarValueMicrons] = useState(100) // Store actual microns value
  const [annotationTool, setAnnotationTool] = useState<string | null>(null)
  const [annotations, setAnnotations] = useState<any[]>([])
  const [unsavedAnnotations, setUnsavedAnnotations] = useState<any[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [hiddenAnnotations, setHiddenAnnotations] = useState<Set<string>>(new Set())
  const [slideInfo, setSlideInfo] = useState<SlideInfo | null>(null)
  const [cellDetections, setCellDetections] = useState<any[]>([])
  const [showAIPanel, setShowAIPanel] = useState(false)

  // Annotation Tool states
  const [showAnnotationTool, setShowAnnotationTool] = useState(false)
  const [annotationMode, setAnnotationMode] = useState<string | null>(null)
  const [manualAnnotations, setManualAnnotations] = useState<any[]>([])
  const [selectedCellIndex, setSelectedCellIndex] = useState<number | null>(null)
  const [editMode, setEditMode] = useState(false)

  // Report Panel state
  const [showReportPanel, setShowReportPanel] = useState(false)

  // Snapshot Panel state
  const [showSnapshotPanel, setShowSnapshotPanel] = useState(false)
  const [snapshotRegionMode, setSnapshotRegionMode] = useState(false)
  const snapshotPanelRef = useRef<any>(null)

  // Multi-View handler - open in new tab
  const handleOpenMultiView = () => {
    if (imageId) {
      const caseId = slideInfo?.properties?.case_id
      const url = `/imaging/multi-view/${imageId}${caseId ? `?caseId=${caseId}` : ''}`
      window.open(url, '_blank')
    }
  }

  // Tooltip for manual annotations
  const [manualAnnotationTooltip, setManualAnnotationTooltip] = useState<{
    visible: boolean
    x: number
    y: number
    annotation: any
  }>({ visible: false, x: 0, y: 0, annotation: null })

  // Auto-open report panel if editReportId is provided
  useEffect(() => {
    if (editReportId) {
      setShowReportPanel(true)
    }
  }, [editReportId])

  // Load annotations from backend
  useEffect(() => {
    if (!imageId) return

    const loadAnnotations = async () => {
      try {
        const response = await fetch(`/api/v1/pathology/annotations/?slide_id=${imageId}`)
        if (response.ok) {
          const data = await response.json()
          const annList = data.results || data
          setAnnotations(annList)
        }
      } catch (error) {
        console.error('Failed to load annotations:', error)
      }
    }

    loadAnnotations()
  }, [imageId])

  // Handle new annotations (don't save automatically)
  const handleAnnotationsChange = (newAnnotations: any[]) => {
    setAnnotations(newAnnotations)

    // Track new unsaved annotations
    if (newAnnotations.length > annotations.length) {
      const latestAnnotation = newAnnotations[newAnnotations.length - 1]
      setUnsavedAnnotations(prev => [...prev, latestAnnotation])
      setHasUnsavedChanges(true)
    }
  }

  // Save all unsaved annotations to backend
  const handleSaveAnnotations = async () => {
    if (unsavedAnnotations.length === 0) {
      toast('No unsaved annotations to save', { icon: 'ℹ️' })
      return
    }

    try {
      let savedCount = 0
      for (const annotation of unsavedAnnotations) {
        const response = await fetch('/api/v1/pathology/annotations/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            slide_id: imageId,
            annotation_type: annotation.type,
            svg_data: annotation.svg,
            text_content: annotation.title || annotation.text || '',
            description: annotation.description || '',
            coordinates: annotation.shapeData || annotation.viewportPoint || annotation.coordinates || {},
            color: '#00ff00',
            stroke_width: 3,
          }),
        })

        if (response.ok) {
          savedCount++
        } else {
          console.error('Failed to save annotation:', response.statusText)
        }
      }

      if (savedCount > 0) {
        toast.success(`${savedCount} ${t('viewer.annotations').toLowerCase()} saved successfully`)
        setUnsavedAnnotations([])
        setHasUnsavedChanges(false)
      } else {
        toast.error(`Failed to save ${t('viewer.annotations').toLowerCase()}`)
      }
    } catch (error) {
      console.error('Error saving annotations:', error)
      toast.error(`${t('common.error')} saving ${t('viewer.annotations').toLowerCase()}`)
    }
  }

  // Clear all annotations
  const handleClearAllAnnotations = async () => {
    try {
      // Get all annotations for this slide
      const response = await fetch(`/api/v1/pathology/annotations/?slide_id=${imageId}`)
      if (response.ok) {
        const data = await response.json()
        const annotationIds = (data.results || data).map((ann: any) => ann.id)

        // Delete all annotations
        for (const id of annotationIds) {
          await fetch(`/api/v1/pathology/annotations/${id}/`, {
            method: 'DELETE',
          })
        }

        setAnnotations([])
        toast.success(`All ${t('viewer.annotations').toLowerCase()} cleared`)
      }
    } catch (error) {
      console.error('Error clearing annotations:', error)
      toast.error(`Failed to clear ${t('viewer.annotations').toLowerCase()}`)
    }
  }

  // Load manual annotations from API
  const loadManualAnnotations = async () => {
    if (!imageId) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `/api/v1/ai/annotations/?slide_id=${imageId}`,
        { headers: { Authorization: `Token ${token}` } }
      )

      if (response.ok) {
        const jsonData = await response.json()
        // Handle both paginated and non-paginated responses
        const data = jsonData.results || jsonData
        const annotationsArray = Array.isArray(data) ? data : []
        setManualAnnotations(annotationsArray)
      }
    } catch (error) {
      console.error('Failed to load manual annotations:', error)
      setManualAnnotations([])
    }
  }

  // Load manual annotations on mount
  useEffect(() => {
    if (imageId) {
      loadManualAnnotations()
    }
  }, [imageId])


  useEffect(() => {
    if (!viewerRef.current || !imageId) return

    const loadSlide = async () => {
      try {
        // Wait for OpenSeadragon to load
        if (!(window as any).OpenSeadragon) {
          await new Promise((resolve) => {
            const checkOSD = setInterval(() => {
              if ((window as any).OpenSeadragon) {
                clearInterval(checkOSD)
                resolve(true)
              }
            }, 100)
          })
        }

        const token = localStorage.getItem('token')
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        }
        if (token) {
          headers['Authorization'] = `Token ${token}`
        }

        const infoResponse = await fetch(`/api/v1/imaging/slides/${imageId}/info/`, {
          headers
        })
        if (!infoResponse.ok) {
          throw new Error(`Failed to load ${t('viewer.imageInfo').toLowerCase()}: ${infoResponse.status}`)
        }

        const info: SlideInfo = await infoResponse.json()
        setSlideInfo(info)

        // Calculate DZI levels
        const maxDimension = Math.max(info.dimensions.width, info.dimensions.height)
        const tileSize = info.tile_size || 256

        // Use max_zoom from backend if available (for pre-generated DZI), otherwise calculate
        const dziLevels = (info as any).max_zoom !== undefined && (info as any).max_zoom > 0
          ? (info as any).max_zoom + 1
          : Math.ceil(Math.log2(maxDimension / tileSize)) + 1

        // All formats now use standard DZI tile URL pattern
        const getTileUrl = (level: number, x: number, y: number) => {
          return `/api/v1/imaging/standalone/dzi/${imageId}_files/${level}/${x}_${y}.jpeg`
        }

        // Double-check viewerRef is still valid before initializing
        if (!viewerRef.current) {
          console.error('❌ ViewerRef became null before OpenSeadragon init')
          return
        }

        // Create DZI tile source for better navigator support
        const tileSource = {
          height: info.dimensions.height,
          width: info.dimensions.width,
          tileSize: tileSize,
          minLevel: 0,
          maxLevel: dziLevels - 1,
          getTileUrl: getTileUrl,
        }


        // Initialize OpenSeadragon with custom tile source
        const viewer = (window as any).OpenSeadragon({
          element: viewerRef.current,
          prefixUrl: '/openseadragon/images/',

          // Custom tile source - use DZI levels as backend expects
          tileSources: tileSource,

          // Viewer settings
          showNavigationControl: false,
          navigationControlAnchor: (window as any).OpenSeadragon.ControlAnchor.TOP_LEFT,
          showFullPageControl: false,
          animationTime: 0.5,
          blendTime: 0.1,
          constrainDuringPan: true,
          minZoomImageRatio: 1.0,  // Không cho zoom nhỏ hơn home zoom
          maxZoomPixelRatio: 10,  // Cho phép zoom sâu tới 100X (tăng từ 2 lên 10)
          visibilityRatio: 0.5,  // Load tiles sớm hơn
          zoomPerScroll: 1.5,  // Tăng tốc độ zoom (từ 1.4 lên 1.5)
          immediateRender: false,
          homeFillsViewer: false,
          defaultZoomLevel: 0,
          smoothTileEdgesMinZoom: 1.5,  // Smooth tiles khi zoom thấp
          wrapHorizontal: false,
          wrapVertical: false,
          compositeOperation: 'source-over',

          // Navigator - disabled, using custom minimap instead
          showNavigator: false,
        })

        // Custom minimap will be rendered separately as React component

        viewerInstanceRef.current = viewer

        // Update zoom info display and scale bar
        const updateZoomInfo = () => {
          if (viewer && info.level_dimensions) {
            const currentZoom = viewer.viewport.getZoom()
            const homeZoom = viewer.viewport.getHomeZoom()
            const zoomRatio = currentZoom / homeZoom

            // Estimate current level
            let estimatedLevel = 0
            let closestDiff = Infinity

            info.level_dimensions.forEach((dims: number[], index: number) => {
              const levelZoomRatio = info.dimensions.width / dims[0]
              const diff = Math.abs(zoomRatio - levelZoomRatio)
              if (diff < closestDiff) {
                closestDiff = diff
                estimatedLevel = index
              }
            })

            // Effective Magnification = Zoom Ratio
            // This makes magnification directly match the zoom level
            // Zoom 1× = 1× mag, Zoom 20× = 20× mag (intuitive and matches scale bar)
            // This is more appropriate for digital pathology viewers where
            // "magnification" represents viewing scale rather than optical magnification
            const effectiveMagnification = zoomRatio

            setZoomInfo({
              zoom: parseFloat(zoomRatio.toFixed(3)), // Use zoom ratio (relative to home), not absolute zoom
              level: estimatedLevel,
              magnification: parseFloat(effectiveMagnification.toFixed(2)),
            })

            // Update scale bar - Vieworks style (fixed 100 pixels width)
            // Scale bar always 100 pixels, value changes based on zoom and MPP

            // Get MPP (microns per pixel) from slide properties
            // Adjusted default to match KFBio viewer behavior
            let calibration = 20.0 // Default fallback adjusted for KFBio-like scale
            if (slideInfo?.properties) {
              const mppX = slideInfo.properties['openslide.mpp-x']
              const mppY = slideInfo.properties['openslide.mpp-y']
              if (mppX) {
                calibration = parseFloat(mppX)
              } else if (mppY) {
                calibration = parseFloat(mppY)
              } else {
                logger.debug('📏 No MPP metadata, using default:', calibration, 'μm/pixel (KFBio-style)')
              }
            }

            // Scale bar: Fixed width (200px physical, but represents half the value)
            const fixedPixelWidth = 200

            // Calculate current microns per pixel at this zoom level
            const micronsPerPixel = calibration / zoomRatio

            // Calculate the actual value - divide by 2 to show half value
            // e.g., 200 pixels = 100 µm (so each pixel represents 0.5 µm in display)
            const scaleValueMicrons = (fixedPixelWidth * micronsPerPixel) / 2

            setScaleBarWidth(fixedPixelWidth)
            setScaleBarValueMicrons(scaleValueMicrons) // Store actual microns value
          }
        }

        viewer.addHandler('open', () => {
          setViewerReady(true)
          setTimeout(() => {
            updateZoomInfo()

            // Default to "Fit" mode (goHome) when opening slide
            // This matches the "Fit" button behavior in zoom menu
            viewer.viewport.goHome(true)
          }, 200)
        })

        viewer.addHandler('zoom', () => {
          updateZoomInfo()
          // Don't update navigator on zoom - it handles itself
        })
        viewer.addHandler('animation-finish', updateZoomInfo)
        viewer.addHandler('pan', () => {
          // Don't update navigator on pan - it handles itself
        })
        // Remove viewport-change handler to prevent navigator reset

      } catch (error) {
        console.error('❌ Failed to load slide:', error)
        toast.error(`${t('common.error')}: Failed to load slide`)
      }
    }

    loadSlide()

    return () => {
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.destroy()
      }
    }
  }, [imageId])

  // Handle bounding box drawing - separate effect that runs when viewer is ready
  useEffect(() => {
    if (!viewerReady || !viewerInstanceRef.current || annotationMode !== 'bbox' || !editMode || selectedCellIndex === null) {
      return
    }

    const viewer = viewerInstanceRef.current
    if (!viewer.canvas) {
      console.warn('⚠️ Viewer canvas not ready yet')
      return
    }

    logger.debug('🎨 Setting up bbox drawing...')

    let startPoint: any = null
    let isDragging = false
    let overlayElement: HTMLDivElement | null = null

    const mouseTracker = new (window as any).OpenSeadragon.MouseTracker({
      element: viewer.canvas,
      pressHandler: (event: any) => {
        if (annotationMode !== 'bbox') return

        event.preventDefaultAction = true
        const webPoint = event.position
        const viewportPoint = viewer.viewport.pointFromPixel(webPoint)
        startPoint = viewer.viewport.viewportToImageCoordinates(viewportPoint)
        isDragging = true

        // Create overlay element for visual feedback
        overlayElement = document.createElement('div')
        overlayElement.style.position = 'absolute'
        overlayElement.style.border = '2px solid #3B82F6'
        overlayElement.style.backgroundColor = 'rgba(59, 130, 246, 0.1)'
        overlayElement.style.pointerEvents = 'none'
        overlayElement.style.zIndex = '1000'
        viewer.canvas.parentElement?.appendChild(overlayElement)
      },
      moveHandler: (event: any) => {
        if (!isDragging || !startPoint || !overlayElement) return

        const webPoint = event.position
        const viewportPoint = viewer.viewport.pointFromPixel(webPoint)
        const currentPoint = viewer.viewport.viewportToImageCoordinates(viewportPoint)

        // Calculate rectangle in viewport coordinates
        const minX = Math.min(startPoint.x, currentPoint.x)
        const minY = Math.min(startPoint.y, currentPoint.y)
        const maxX = Math.max(startPoint.x, currentPoint.x)
        const maxY = Math.max(startPoint.y, currentPoint.y)

        const topLeft = viewer.viewport.imageToViewportCoordinates(minX, minY)
        const bottomRight = viewer.viewport.imageToViewportCoordinates(maxX, maxY)

        const pixelTopLeft = viewer.viewport.viewportToWindowCoordinates(topLeft)
        const pixelBottomRight = viewer.viewport.viewportToWindowCoordinates(bottomRight)

        overlayElement.style.left = `${pixelTopLeft.x}px`
        overlayElement.style.top = `${pixelTopLeft.y}px`
        overlayElement.style.width = `${pixelBottomRight.x - pixelTopLeft.x}px`
        overlayElement.style.height = `${pixelBottomRight.y - pixelTopLeft.y}px`
      },
      releaseHandler: (event: any) => {
        if (!isDragging || !startPoint) return

        const webPoint = event.position
        const viewportPoint = viewer.viewport.pointFromPixel(webPoint)
        const endPoint = viewer.viewport.viewportToImageCoordinates(viewportPoint)

        // Calculate bounding box in image coordinates
        const bbox = {
          x: Math.min(startPoint.x, endPoint.x),
          y: Math.min(startPoint.y, endPoint.y),
          width: Math.abs(endPoint.x - startPoint.x),
          height: Math.abs(endPoint.y - startPoint.y)
        }

        // Only save if box is larger than 10x10 pixels
        if (bbox.width > 10 && bbox.height > 10) {
          // Update the selected cell's bbox
          if (selectedCellIndex !== null) {
            const updatedCells = [...cellDetections]
            updatedCells[selectedCellIndex] = {
              ...updatedCells[selectedCellIndex],
              bbox: bbox
            }
            setCellDetections(updatedCells)
            toast.success('Bounding box updated!')
            logger.debug('Bounding box updated:', bbox)
            // Exit edit mode after updating
            setEditMode(false)
            setAnnotationMode(null)
          }
        }

        // Cleanup
        if (overlayElement) {
          overlayElement.remove()
          overlayElement = null
        }
        startPoint = null
        isDragging = false
      }
    })

    mouseTracker.setTracking(true)

    return () => {
      logger.debug('🧹 Cleaning up bbox drawing')
      mouseTracker.setTracking(false)
      mouseTracker.destroy()
    }
  }, [viewerReady, annotationMode, editMode, selectedCellIndex])

  // Handle snapshot region selection
  const handleSnapshotRegionSelected = useCallback((bounds: any) => {
    logger.debug('📸 Snapshot region selected, triggering capture:', bounds)

    // Call capture function from snapshot panel
    if (snapshotPanelRef.current && snapshotPanelRef.current.handleCapture) {
      snapshotPanelRef.current.handleCapture(bounds)
    }

    // End region mode
    setSnapshotRegionMode(false)
  }, [])

  return (
    <div className="w-full h-screen bg-black flex relative">
      {/* Main Viewer Area - Takes remaining space */}
      <div className={`flex-1 flex flex-col relative transition-all duration-300 ${showReportPanel ? 'w-1/2' : 'w-full'}`}>
      {/* Viewer Controls */}
      {viewerReady && viewerInstanceRef.current && (
        <ABTStyleControls
          viewer={viewerInstanceRef.current}
          onAnnotationToolChange={setAnnotationTool}
          selectedAnnotationTool={annotationTool}
          onClearAnnotations={handleClearAllAnnotations}
          onSaveAnnotations={handleSaveAnnotations}
          hasUnsavedAnnotations={hasUnsavedChanges}
          currentImageId={imageId}
          hiddenAnnotations={hiddenAnnotations}
          onToggleAnnotationVisibility={(id) => {
            setHiddenAnnotations(prev => {
              const newSet = new Set(prev)
              if (newSet.has(id)) {
                newSet.delete(id)
              } else {
                newSet.add(id)
              }
              return newSet
            })
          }}
          onToggleAIPanel={() => setShowAIPanel(!showAIPanel)}
          showAIPanel={showAIPanel}
          onToggleAnnotationTool={() => setShowAnnotationTool(!showAnnotationTool)}
          showAnnotationTool={showAnnotationTool}
          onToggleReportPanel={() => setShowReportPanel(!showReportPanel)}
          showReportPanel={showReportPanel}
          onToggleSnapshotPanel={() => setShowSnapshotPanel(!showSnapshotPanel)}
          showSnapshotPanel={showSnapshotPanel}
          onToggleMultiView={handleOpenMultiView}
          showMultiView={false}
        />
      )}

      {/* Image Info - Bottom Left */}
      {viewerReady && slideInfo && (
        <div className="absolute bottom-4 left-4 z-10 bg-white bg-opacity-90 px-3 py-2 rounded shadow-lg">
          <div className="text-xs space-y-1">
            <div className="font-bold text-gray-800 border-b border-gray-300 pb-1 mb-1">{t('viewer.imageInfo')}</div>
            <div className="text-gray-700">
              <span className="font-semibold">{t('viewer.dimensions')}:</span> {slideInfo.dimensions.width.toLocaleString()} × {slideInfo.dimensions.height.toLocaleString()} px
            </div>
            <div className="text-gray-700">
              <span className="font-semibold">{t('viewer.levels')}:</span> {slideInfo.levels}
            </div>
            <div className="text-gray-700">
              <span className="font-semibold">{t('viewer.level')}:</span> {zoomInfo.level}
            </div>
            <div className="text-gray-700">
              <span className="font-semibold">{t('viewer.zoom')}:</span> {zoomInfo.zoom.toFixed(1)}×
            </div>
            <div className="text-gray-700">
              <span className="font-semibold">Tile Size:</span> {slideInfo.tile_size} px
            </div>
            <div className="text-gray-700">
              <span className="font-semibold">{t('viewer.magnification')}:</span> {zoomInfo.magnification.toFixed(1)}×
            </div>
          </div>
        </div>
      )}

      {/* Scale Bar - Graduated ruler with 5 divisions */}
      {viewerReady && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          <div className="flex flex-col items-center">
            {/* Scale bar container with labels */}
            <div className="relative" style={{ width: `${scaleBarWidth}px`, height: '30px' }}>
              {/* Main horizontal line */}
              <div className="absolute bottom-8 left-0 right-0 h-0.5 bg-black"></div>

              {/* Generate 6 marks (0, 20, 40, 60, 80, 100) */}
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const position = (scaleBarWidth / 5) * i

                // Use stored microns value for accurate calculation
                const markValueInMicrons = (scaleBarValueMicrons / 5) * i

                // Determine unit and display value
                let displayValue = ''
                if (scaleBarValueMicrons >= 1000) {
                  // Use mm
                  const markValueInMm = markValueInMicrons / 1000
                  if (i === 5) {
                    displayValue = Number.isInteger(markValueInMm)
                      ? `${markValueInMm}mm`
                      : `${markValueInMm.toFixed(1)}mm`
                  } else {
                    displayValue = Number.isInteger(markValueInMm)
                      ? `${markValueInMm}`
                      : `${markValueInMm.toFixed(1)}`
                  }
                } else if (scaleBarValueMicrons >= 1) {
                  // Use μm
                  if (i === 5) {
                    displayValue = Number.isInteger(markValueInMicrons)
                      ? `${markValueInMicrons}μm`
                      : `${markValueInMicrons.toFixed(1)}μm`
                  } else {
                    displayValue = Number.isInteger(markValueInMicrons)
                      ? `${markValueInMicrons}`
                      : `${markValueInMicrons.toFixed(1)}`
                  }
                } else {
                  // Use nm
                  const markValueInNm = markValueInMicrons * 1000
                  if (i === 5) {
                    displayValue = `${Math.round(markValueInNm)}nm`
                  } else {
                    displayValue = `${Math.round(markValueInNm)}`
                  }
                }

                return (
                  <div key={i} className="absolute bottom-8" style={{ left: `${position}px` }}>
                    {/* Vertical mark - black */}
                    <div className="absolute -translate-x-1/2 w-0.5 h-3 bg-black"></div>
                    {/* Number label */}
                    <div
                      className="absolute -translate-x-1/2 top-3 text-xs font-semibold text-black whitespace-nowrap"
                    >
                      {displayValue}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Viewer */}
      <div ref={viewerRef} className="w-full h-full" />

      {/* Custom Minimap */}
      {viewerReady && viewerInstanceRef.current && slideInfo && (
        <SimpleMinimap
          viewer={viewerInstanceRef.current}
          imageId={imageId as string}
          bounds={slideInfo.bounds}
          dimensions={slideInfo.dimensions}
        />
      )}

      {/* Saved Annotations Overlay - Using OpenSeadragon Overlay API */}
      {viewerReady && viewerInstanceRef.current && (
        <SavedAnnotationsOverlay
          viewer={viewerInstanceRef.current}
          annotations={annotations}
          hiddenAnnotations={hiddenAnnotations}
          onAnnotationClick={(ann) => {
            logger.debug('Annotation clicked:', ann)
          }}
        />
      )}

      {/* Annotation Drawing Overlay */}
      {viewerReady && (
        <AnnotationOverlay
          viewer={viewerInstanceRef.current}
          tool={annotationTool}
          onAnnotationsChange={handleAnnotationsChange}
        />
      )}

      {/* Snapshot Region Selection Overlay */}
      {viewerReady && viewerInstanceRef.current && (
        <SnapshotRegionOverlay
          viewer={viewerInstanceRef.current}
          isActive={snapshotRegionMode}
          onRegionSelected={handleSnapshotRegionSelected}
        />
      )}

      {/* Annotation Indicator */}
      {annotationTool && (
        <div className="absolute top-20 left-4 z-10 bg-green-500 text-white px-3 py-2 rounded shadow-lg text-sm font-semibold">
          Drawing: {annotationTool.toUpperCase()} - Click and drag on image
        </div>
      )}

      {/* Snapshot Region Selection Indicator */}
      {snapshotRegionMode && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none">
          <div className="bg-purple-600 text-white px-6 py-4 rounded-lg shadow-2xl text-center">
            <div className="text-xl font-bold mb-2">✏️ Chế độ chọn vùng chụp</div>
            <div className="text-sm">Click và kéo trên ảnh để vẽ hình chữ nhật</div>
          </div>
        </div>
      )}

      {/* Snapshot Manager Panel */}
      {showSnapshotPanel && imageId && viewerInstanceRef.current && (
        <div className="absolute top-4 right-20 z-10">
          <SnapshotManagerPanel
            ref={snapshotPanelRef}
            slideId={imageId}
            viewer={viewerInstanceRef.current}
            onClose={() => setShowSnapshotPanel(false)}
            onStartRegionSelect={() => {
              console.log('📸 Starting region select mode')
              setSnapshotRegionMode(true)
            }}
            onEndRegionSelect={() => {
              console.log('📸 Ending region select mode')
              setSnapshotRegionMode(false)
            }}
          />
        </div>
      )}

      {/* Annotation Tool Panel */}
      {showAnnotationTool && imageId && (
        <div className="absolute top-4 left-4 z-10 w-96 max-h-[80vh] overflow-y-auto">
          <AnnotationToolPanel
            slideId={imageId}
            detectedCells={cellDetections}
            selectedCellIndex={selectedCellIndex}
            onCellSelect={(index) => {
              setSelectedCellIndex(index)
              if (index !== null && cellDetections[index]) {
                // Navigate to selected cell
                const cell = cellDetections[index]
                const viewer = viewerInstanceRef.current
                if (viewer && slideInfo) {
                  const centerX = cell.bbox.x + cell.bbox.width / 2
                  const centerY = cell.bbox.y + cell.bbox.height / 2
                  const viewportX = centerX / slideInfo.dimensions.width
                  const viewportY = centerY / slideInfo.dimensions.height
                  const point = new (window as any).OpenSeadragon.Point(viewportX, viewportY)
                  viewer.viewport.panTo(point, true)
                }
              }
            }}
            onCellsDetected={(cells) => {
              // Update cells when detection completes
              logger.debug('Cells detected:', cells.length)
              setCellDetections(cells)
            }}
            onEditModeToggle={(enabled) => {
              setEditMode(enabled)
              if (enabled) {
                setAnnotationMode('bbox')
              } else {
                setAnnotationMode(null)
              }
            }}
            editMode={editMode}
          />
        </div>
      )}


      {/* AI Diagnosis Panel */}
      {showAIPanel && imageId && (
        <div className="absolute top-4 right-20 z-10 w-96 max-h-[80vh] overflow-y-auto">
          <AIDiagnosisPanel
            slideId={imageId}
            onDetectionsLoad={(cells) => {
              setCellDetections(cells)
            }}
            onCellClick={(cell) => {
              // Zoom to cell location
              const viewer = viewerInstanceRef.current
              if (!viewer || !slideInfo) {
                console.error('Viewer or slideInfo not available')
                return
              }

              logger.debug('Navigating to cell:', cell)
              logger.debug('Slide dimensions:', slideInfo.dimensions)

              // Cell bbox is in pixel coordinates of the full-resolution image
              const centerX = cell.bbox.x + cell.bbox.width / 2
              const centerY = cell.bbox.y + cell.bbox.height / 2

              logger.debug('Cell center in pixels:', centerX, centerY)

              // Convert pixel coordinates to normalized viewport coordinates (0-1 range)
              // OpenSeadragon viewport coordinates are relative to image dimensions
              const viewportX = centerX / slideInfo.dimensions.width
              const viewportY = centerY / slideInfo.dimensions.height

              logger.debug('Viewport coordinates:', viewportX, viewportY)

              // Create point in viewport coordinate system
              const point = new (window as any).OpenSeadragon.Point(viewportX, viewportY)

              // Set zoom to 40x magnification
              const baseMag = slideInfo.properties?.['openslide.objective-power']
                ? parseFloat(slideInfo.properties['openslide.objective-power'])
                : 20.0
              const targetZoom = viewer.viewport.getMaxZoom() * (40.0 / baseMag) / 20.0

              logger.debug('Base mag:', baseMag, 'Target zoom:', targetZoom)

              // Pan to cell center first, then zoom
              viewer.viewport.panTo(point, true)
              setTimeout(() => {
                viewer.viewport.zoomTo(targetZoom, point, true)
              }, 100)

              toast.success(`Navigated to ${cell.cell_class.toUpperCase()} cell`)
            }}
          />
        </div>
      )}

      {/* Cell Detection Overlays */}
      {viewerReady && viewerInstanceRef.current && cellDetections.length > 0 && showAnnotationTool && (
        <svg
          className="absolute top-0 left-0 w-full h-full"
          style={{ zIndex: 5, pointerEvents: 'none' }}
        >
          {cellDetections.map((cell, idx) => {
            const viewer = viewerInstanceRef.current
            if (!viewer) return null

            // Convert slide coordinates to viewport coordinates
            const point = viewer.viewport.imageToViewportCoordinates(
              cell.bbox.x,
              cell.bbox.y
            )
            const size = viewer.viewport.imageToViewportCoordinates(
              cell.bbox.x + cell.bbox.width,
              cell.bbox.y + cell.bbox.height
            )

            const pixelPoint = viewer.viewport.viewportToViewerElementCoordinates(point)
            const pixelSize = viewer.viewport.viewportToViewerElementCoordinates(size)

            const width = pixelSize.x - pixelPoint.x
            const height = pixelSize.y - pixelPoint.y

            // Color based on selection and abnormality
            const isSelected = selectedCellIndex === idx
            const color = isSelected
              ? '#facc15' // Yellow for selected
              : cell.is_abnormal
              ? '#ef4444' // Red for abnormal
              : '#22c55e' // Green for normal
            const strokeWidth = isSelected ? 4 : cell.is_abnormal ? 3 : 2

            return (
              <g key={idx} style={{ pointerEvents: 'auto', cursor: 'pointer' }}>
                <rect
                  x={pixelPoint.x}
                  y={pixelPoint.y}
                  width={width}
                  height={height}
                  fill={isSelected ? 'rgba(250, 204, 21, 0.1)' : 'none'}
                  stroke={color}
                  strokeWidth={strokeWidth}
                  opacity={0.9}
                  onClick={() => {
                    setSelectedCellIndex(idx)
                    // Navigate to cell
                    const centerX = cell.bbox.x + cell.bbox.width / 2
                    const centerY = cell.bbox.y + cell.bbox.height / 2
                    const viewportX = centerX / slideInfo!.dimensions.width
                    const viewportY = centerY / slideInfo!.dimensions.height
                    const point = new (window as any).OpenSeadragon.Point(viewportX, viewportY)
                    viewer.viewport.panTo(point, true)
                  }}
                />
                <text
                  x={pixelPoint.x}
                  y={pixelPoint.y - 5}
                  fill={color}
                  fontSize="12"
                  fontWeight="bold"
                  style={{
                    textShadow: '0 0 3px black'
                  }}
                >
                  {cell.cell_class} {(cell.confidence * 100).toFixed(0)}%
                </text>
              </g>
            )
          })}
        </svg>
      )}

      {/* Manual Annotations Overlay */}
      {viewerReady && viewerInstanceRef.current && manualAnnotations.length > 0 && (
        <svg
          className="absolute top-0 left-0 w-full h-full"
          style={{ zIndex: 6, pointerEvents: 'auto' }}
        >
          {manualAnnotations.map((annotation) => {
            const viewer = viewerInstanceRef.current
            if (!viewer) return null

            // Convert slide coordinates to viewport coordinates
            const point = viewer.viewport.imageToViewportCoordinates(
              annotation.bbox.x,
              annotation.bbox.y
            )
            const size = viewer.viewport.imageToViewportCoordinates(
              annotation.bbox.x + annotation.bbox.width,
              annotation.bbox.y + annotation.bbox.height
            )

            const pixelPoint = viewer.viewport.viewportToViewerElementCoordinates(point)
            const pixelSize = viewer.viewport.viewportToViewerElementCoordinates(size)

            const width = Math.abs(pixelSize.x - pixelPoint.x)
            const height = Math.abs(pixelSize.y - pixelPoint.y)

            // Color based on verification status
            const color = annotation.is_verified ? '#10B981' : '#3B82F6' // green if verified, blue otherwise
            const strokeWidth = annotation.is_verified ? 3 : 2

            return (
              <g
                key={annotation.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  setManualAnnotationTooltip({
                    visible: true,
                    x: e.clientX,
                    y: e.clientY,
                    annotation: annotation
                  })
                }}
                onMouseMove={(e) => {
                  setManualAnnotationTooltip(prev => ({
                    ...prev,
                    x: e.clientX,
                    y: e.clientY
                  }))
                }}
                onMouseLeave={() => {
                  setManualAnnotationTooltip({ visible: false, x: 0, y: 0, annotation: null })
                }}
              >
                <rect
                  x={pixelPoint.x}
                  y={pixelPoint.y}
                  width={width}
                  height={height}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                  opacity={0.9}
                  strokeDasharray={annotation.is_verified ? '0' : '5,5'}
                />
                <text
                  x={pixelPoint.x}
                  y={pixelPoint.y - 5}
                  fill={color}
                  fontSize="11"
                  fontWeight="bold"
                  style={{
                    textShadow: '0 0 3px black, 0 0 5px black'
                  }}
                >
                  {annotation.cell_class.toUpperCase().replace('_', '-')}
                  {annotation.is_verified && ' ✓'}
                </text>
              </g>
            )
          })}
        </svg>
      )}

      {/* Manual Annotation Tooltip */}
      {manualAnnotationTooltip.visible && manualAnnotationTooltip.annotation && (
        <div
          className="fixed z-50 bg-blue-900 text-white px-3 py-2 rounded-lg shadow-2xl max-w-xs pointer-events-none"
          style={{
            left: `${manualAnnotationTooltip.x + 15}px`,
            top: `${manualAnnotationTooltip.y + 15}px`,
          }}
        >
          <div className="font-bold text-sm mb-1 text-blue-200">
            {manualAnnotationTooltip.annotation.cell_class.toUpperCase().replace('_', '-')}
          </div>
          <div className="text-xs text-gray-300 space-y-1">
            <div>Quality: {manualAnnotationTooltip.annotation.quality}</div>
            <div>Confidence: {manualAnnotationTooltip.annotation.confidence_level}/5</div>
            <div>
              Status: {manualAnnotationTooltip.annotation.is_verified ? '✓ Verified' : 'Unverified'}
            </div>
            {manualAnnotationTooltip.annotation.notes && (
              <div className="mt-2 pt-1 border-t border-blue-700 text-gray-200">
                {manualAnnotationTooltip.annotation.notes}
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      {/* Close Main Viewer Area */}

      {/* Cytology Report Panel - Right Side */}
      {showReportPanel && imageId && (
        <div className="w-1/2 h-screen flex-shrink-0">
          <CytologyReportPanel
            slideId={imageId}
            annotations={annotations}
            editReportId={editReportId || undefined}
            onClose={() => setShowReportPanel(false)}
          />
        </div>
      )}
    </div>
  )
}

export default ImprovedSVSViewer
logger.debug('=== VIEWER VERSION: 2025-10-30-v2 ===')
